import ImageBlock from './ImagePageItem';
import TextBlock from './TextPageItem';
import VideoBlock from './VideoPageItem';
import { type PageItem, PageItemType } from '@/types';
import { YoutubePageItem } from './YoutubePageItem';
import BookingListPageItem from './BookingListPageItem';
import BookingPageItem from './BookingPageItem';
import CarouselBlock from './CarouselPageItem';
import { EmbeddedPageItem } from './EmbeddedPageItem';
import { usePlaybackContext } from '../PlaybackContext';
import { normalizePageItemSettings } from '@/utils/normalizePageItemSettings';

interface IBlockProps {
  pageItem: PageItem;
  rotation?: number;
  isActive?: boolean;
  inverted?: boolean;
}

type VideoSettings = {
  src: string;
  objectFit?: 'contain' | 'cover';
};

const blocks = {
  [PageItemType.Image]: ImageBlock,
  [PageItemType.Text]: TextBlock,
  [PageItemType.Youtube]: YoutubePageItem,
  [PageItemType.BookingList]: BookingListPageItem,
  [PageItemType.Booking]: BookingPageItem,
  [PageItemType.Carousel]: CarouselBlock,
  [PageItemType.Embedded]: EmbeddedPageItem,
} as const;

export function PageItem({ pageItem, rotation, isActive, inverted }: IBlockProps) {
  const { type, settings, pageId } = pageItem;
  const { useFullVideoDuration, onVideoEnded, onVideoFailed, onVideoPlaying } = usePlaybackContext();

  if (type === PageItemType.Video) {
    const parsedSettings = normalizePageItemSettings(type, settings) as VideoSettings;

    return (
      <VideoBlock
        {...parsedSettings}
        rotation={rotation}
        isActive={isActive}
        loop={!useFullVideoDuration}
        onEnded={useFullVideoDuration ? onVideoEnded : undefined}
        // Unlike onEnded these are wired up regardless of the duration mode.
        // A failure needs reporting whichever mode the page is in; it is
        // PlayList that decides whether the playlist should move on.
        onFailed={onVideoFailed}
        onPlaying={onVideoPlaying}
      />
    );
  }

  const BlockComponent = blocks?.[type as keyof typeof blocks];

  if (!BlockComponent) return <span>Unknown block {type}</span>;

  const normalizedSettings = normalizePageItemSettings(type, settings);

  return <BlockComponent {...({ ...normalizedSettings } as any)} pageId={pageId} rotation={rotation} isActive={isActive} inverted={inverted} />;
}
